import { useLocalStorage } from '@vueuse/core'
import { onMounted, unref } from 'vue'
import { MaybeRef } from '@/types/types'
import { DistanceUnit } from '@/utils/enums'
import { getLocalStorageString, isBrowser } from '@/utils/utility-manager'

export interface LocalTripSettings {
  distanceUnit: DistanceUnit
  temperatureUnit: 'celsius' | 'fahrenheit'
  mapSettings: {
    showRouteLabels: boolean
    showRoutes: boolean
    mapType: 'roadmap' | 'satellite' | 'terrain'
  }
}

const defaultTripSettings: LocalTripSettings = {
  distanceUnit: DistanceUnit.KILOMETERS,
  temperatureUnit: 'celsius',
  mapSettings: {
    showRouteLabels: true,
    showRoutes: true,
    mapType: 'roadmap',
  },
}

export function useLocalTripSettings(tripId: MaybeRef<string>) {
  const tripIdValue = unref(tripId)
  const key = `trip-settings-${tripIdValue}`

  const alreadyMigrated = isBrowser() && localStorage.getItem(key) !== null

  const tripSettings = useLocalStorage<LocalTripSettings>(
    key,
    defaultTripSettings,
  )

  onMounted(() => {
    if (isBrowser() && !alreadyMigrated) {
      migrateSettings()
    }
  })

  function migrateSettings() {
    // Old local storage keys
    const oldMapType = getLocalStorageString(
      `kLambusMapType_${tripIdValue}`,
      defaultTripSettings.mapSettings.mapType,
    ) as 'roadmap' | 'satellite' | 'terrain'
    const oldDistanceUnit = getLocalStorageString(
      `kLambusShowPolylineDistanceMetric_${tripIdValue}`,
      defaultTripSettings.distanceUnit,
    ) as DistanceUnit
    const oldShowDistance =
      getLocalStorageString(
        `kLambusShowPolylineShowDistance_${tripIdValue}`,
        'true',
      ) === 'true'
    const oldConnectWaypoints =
      getLocalStorageString(`kLambusShowPolyline_${tripIdValue}`, 'true') ===
      'true'
    const oldWeatherMetric = getLocalStorageString(
      `kLambusWeatherMetric`,
      defaultTripSettings.temperatureUnit === 'fahrenheit'
        ? 'imperial'
        : 'metric',
    )

    // Map old values to new structure
    const migratedSettings: LocalTripSettings = {
      distanceUnit: oldDistanceUnit,
      temperatureUnit:
        oldWeatherMetric === 'imperial' ? 'fahrenheit' : 'celsius',
      mapSettings: {
        showRouteLabels: oldShowDistance,
        showRoutes: oldConnectWaypoints,
        mapType: oldMapType,
      },
    }

    tripSettings.value = migratedSettings

    // Remove old localStorage keys, but not weather metric as it's global
    const keysToRemove = [
      `kLambusMapType_${tripIdValue}`,
      `kLambusShowPolylineDistanceMetric_${tripIdValue}`,
      `kLambusShowPolylineShowDistance_${tripIdValue}`,
      `kLambusShowPolyline_${tripIdValue}`,
    ]

    keysToRemove.forEach((oldKey) => {
      localStorage.removeItem(oldKey)
    })
  }

  function resetTripSettings() {
    tripSettings.value = defaultTripSettings
  }

  return { tripSettings, resetTripSettings, migrateSettings }
}
